import axios from 'axios';
import { Form } from 'formik';
import React, { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { stepFeedbackFulfilled } from '../../../../../actions/registerShipmentActions';
import { REGISTER_SHIPMENT_FEEDBACK } from '../../../../../constants/paths/api';
import Button from '../../../../base/button';
import FormikFieldWrapper from '../../../../base/forms/formikFieldWrapper';
import { StyledFormikTextArea } from '../../../../base/forms/input';
import { Column, Columns } from '../../../../base/grid';
import { RawLoader } from '../../../../loading/loading';
import * as StyledStepFeedbackForm from './stepFeedbackForm.styles';

interface StepFeedbackFormValues {
	rating: number;
	feedback: string;
}

interface StepFeedbackInfo {
	shipmentId: string;
	clientId: string;
	domestic: boolean;
	loggedIn: boolean;
}

interface Props {
	values: StepFeedbackFormValues;
	setFieldValue: (field: string, value: number, shouldValidate?: boolean) => void;
	info: StepFeedbackInfo;
	showFullForm?: boolean;
}

const StepFeedbackForm: FC<Props> = ({ values, setFieldValue, info, showFullForm = false }) => {
	const dispatch = useDispatch();
	const ref = useRef<HTMLDivElement>(null);
	const { t } = useTranslation('registerShipment');
	const [loading, setLoading] = useState<boolean>(false);
	// Local state to track hover effect on stars
	const [hoverRating, setHoverRating] = useState<number>(0);
	// Local state to track if the submission was successful
	const [submitted, setSubmitted] = useState<boolean>(false);
	const textBoxRef = useRef(null);

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setLoading(true);
		dispatch(stepFeedbackFulfilled(values));
		const model = {
			shipmentId: info.shipmentId,
			clientId: info.clientId,
			domestic: info.domestic,
			loggedIn: info.loggedIn,
			feedbackRating: values.rating,
			feedbackText: values.feedback,
		};
		try {
			await axios.post(`${REGISTER_SHIPMENT_FEEDBACK}`, model);
			setSubmitted(true);
		} catch (err) {
			// empty
		}
		setLoading(false);
	};

	if (loading) {
		return (
			<StyledStepFeedbackForm.Wrapper>
				<RawLoader />
			</StyledStepFeedbackForm.Wrapper>
		);
	}

	// Render a success screen if the submission was successful
	if (submitted) {
		return (
			<StyledStepFeedbackForm.Wrapper>
				<h2>{t('shared.success-title')}</h2>
				<p>{t('shared.success-message')}</p>
			</StyledStepFeedbackForm.Wrapper>
		);
	}

	return (
		<div ref={ref}>
			<Form onSubmit={handleSubmit}>
				<StyledStepFeedbackForm.Wrapper>
					<Columns>
						<Column marginBottom={0}>
							<StyledStepFeedbackForm.Stars>
								{[1, 2, 3, 4, 5].map(star => (
									<span
										key={star}
										onMouseEnter={() => {
											setHoverRating(star);
										}}
										onMouseLeave={() => {
											setHoverRating(0);
										}}
										onClick={() => {
											setFieldValue('rating', star);
											if (textBoxRef.current) {
												textBoxRef.current.focus();
											}
										}}
										style={{
											cursor: 'pointer',
											fontSize: '5rem',
											margin: '0 0.5rem 0 0.5rem',
											transition: 'transform 0.2s ease, color 0.2s ease',
											transform: hoverRating >= star ? 'scale(1.1)' : 'scale(1)',
											color: Math.max(values.rating, hoverRating) >= star ? 'red' : 'lightgray',
										}}>
										★
									</span>
								))}
							</StyledStepFeedbackForm.Stars>
						</Column>
						<Column
							style={{
								overflow: 'hidden',
								maxHeight: showFullForm || values.rating !== 0 ? '1000px' : '0px',
								transition: 'max-height 0.5s ease-in-out',
								marginTop: showFullForm || values.rating !== 0 ? '1rem' : '0',
							}}>
							<FormikFieldWrapper
								name="comments"
								label={`${t('forms:feedbackLabel')}`}
								smallLabel
								languageComponent="generalForm">
								<StyledFormikTextArea
									innerRef={textBoxRef}
									id="feedback"
									name="feedback"
									type="text"
									component="textarea"
									value={values.feedback}
									onChange={e => setFieldValue('feedback', e.target.value)}
									padding="1rem"
									placeholder={t('forms:feedbackPlaceholder')}
								/>
							</FormikFieldWrapper>
							<StyledStepFeedbackForm.ButtonWrapper>
								<Button primary type="submit" disabled={values.rating === 0}>
									{t('shared.conformation')}
								</Button>
							</StyledStepFeedbackForm.ButtonWrapper>
						</Column>
					</Columns>
				</StyledStepFeedbackForm.Wrapper>
			</Form>
		</div>
	);
};

export default StepFeedbackForm;
